/**
 * Asset Viewer Controller
 * Manages the image reveal animation sequence and 3D model display
 *
 * Animation flow:
 * 1. Show B&W image
 * 2. Color reveal (circle clip-path, 3 seconds)
 * 3. Fade to black
 * 4. Fade back revealing 3D model with turntable
 * 5. Play audio
 */

import { getBuilding, getAssetByIndex, getAssetCount, hasAssets } from './buildingsLibrary.js';
import { getCurrentLanguage } from './translations.js';

// Animation timing constants (in ms)
const REVEAL_DURATION = 3000;       // B&W to color reveal
const FADE_DURATION = 800;          // Fade to black duration
const MODEL_SHOW_DELAY = 400;       // Delay before showing 3D model after fade

// State
let currentBuildingId = null;
let currentAssetIndex = 0;
let currentAsset = null;
let isAnimating = false;
let audioElement = null;

// DOM Elements (cached on init)
let assetViewerScreen = null;
let imageRevealContainer = null;
let imageRevealWrapper = null;
let blurBgImage = null;
let coloredImage = null;
let maskedImage = null;
let fadeOverlay = null;
let asset3dContainer = null;
let assetInfo = null;
let assetNameCapsule = null;
let assetNameEl = null;
let audioMuteBtn = null;
let assetCurrentEl = null;
let assetTotalEl = null;
let assetPrevBtn = null;
let assetNextBtn = null;
let assetBackBtn = null;

// Reference to canvas container (the actual Three.js container)
let canvasContainer = null;

// AR button elements
let assetArButton = null;
let assetIosArLink = null;
let assetArContainer = null;

// Info popup elements
let assetNameHint = null;
let assetInfoPopup = null;
let assetInfoPopupClose = null;
let assetInfoPopupTitle = null;
let assetInfoPopupText = null;

// Asset list popup elements
let assetCounterCircle = null;
let assetListPopup = null;
let assetListPopupClose = null;
let assetListPopupTitle = null;
let assetListPopupItems = null;

/**
 * Initialize the asset viewer
 */
export function initAssetViewer() {
  // Cache DOM elements
  assetViewerScreen = document.getElementById('asset-viewer-screen');
  imageRevealContainer = document.getElementById('image-reveal-container');
  imageRevealWrapper = document.getElementById('image-reveal-wrapper');
  blurBgImage = document.getElementById('blur-bg-image');
  coloredImage = document.getElementById('colored-image');
  maskedImage = document.getElementById('masked-image');
  fadeOverlay = document.getElementById('fade-overlay');
  asset3dContainer = document.getElementById('asset-3d-container');
  assetInfo = document.getElementById('asset-top-bar');
  assetNameCapsule = document.getElementById('asset-name-capsule');
  assetNameEl = document.getElementById('asset-name');
  audioMuteBtn = document.getElementById('audio-mute-btn');
  assetCurrentEl = document.getElementById('asset-current');
  assetTotalEl = document.getElementById('asset-total');
  assetPrevBtn = document.getElementById('asset-prev-btn');
  assetNextBtn = document.getElementById('asset-next-btn');
  assetBackBtn = document.getElementById('asset-back-btn');
  audioElement = document.getElementById('asset-audio');
  canvasContainer = document.getElementById('canvas-container');
  assetArButton = document.getElementById('asset-ar-button');
  assetIosArLink = document.getElementById('asset-ios-ar-link');
  assetArContainer = document.getElementById('asset-ar-container');
  assetNameHint = document.getElementById('asset-name-hint');
  assetInfoPopup = document.getElementById('asset-info-popup');
  assetInfoPopupClose = document.getElementById('asset-info-popup-close');
  assetInfoPopupTitle = document.getElementById('asset-info-popup-title');
  assetInfoPopupText = document.getElementById('asset-info-popup-text');

  // Asset list popup elements
  assetCounterCircle = document.getElementById('asset-counter-circle');
  assetListPopup = document.getElementById('asset-list-popup');
  assetListPopupClose = document.getElementById('asset-list-popup-close');
  assetListPopupTitle = document.getElementById('asset-list-popup-title');
  assetListPopupItems = document.getElementById('asset-list-popup-items');

  // Set up event listeners
  if (assetPrevBtn) {
    assetPrevBtn.addEventListener('click', () => navigateAsset(-1));
  }
  if (assetNextBtn) {
    assetNextBtn.addEventListener('click', () => navigateAsset(1));
  }
  if (assetBackBtn) {
    assetBackBtn.addEventListener('click', goBackToBuildings);
  }
  if (audioMuteBtn) {
    audioMuteBtn.addEventListener('click', toggleAudioMute);
  }
  if (assetNameCapsule) {
    assetNameCapsule.addEventListener('click', openInfoPopup);
  }
  if (assetInfoPopupClose) {
    assetInfoPopupClose.addEventListener('click', closeInfoPopup);
  }
  if (assetInfoPopup) {
    assetInfoPopup.addEventListener('click', (e) => {
      if (e.target === assetInfoPopup) closeInfoPopup();
    });
  }
  if (assetCounterCircle) {
    assetCounterCircle.addEventListener('click', openAssetList);
  }
  if (assetListPopupClose) {
    assetListPopupClose.addEventListener('click', closeAssetList);
  }
  if (assetListPopup) {
    assetListPopup.addEventListener('click', (e) => {
      if (e.target === assetListPopup) closeAssetList();
    });
  }

  console.log('[AssetViewer] Initialized');
}

/**
 * Open the asset viewer for a specific building
 */
export function openAssetViewer(buildingId, startIndex = 0) {
  const building = getBuilding(buildingId);
  if (!building) {
    console.error('[AssetViewer] Building not found:', buildingId);
    return;
  }

  if (!hasAssets(buildingId)) {
    console.warn('[AssetViewer] Building has no assets:', buildingId);
    return;
  }

  currentBuildingId = buildingId;
  currentAssetIndex = startIndex;

  // Show the asset viewer screen
  assetViewerScreen.classList.add('visible');

  // Show top bar (back button, name, counter)
  if (assetInfo) assetInfo.classList.remove('hidden');

  // Update total count
  const total = getAssetCount(buildingId);
  if (assetTotalEl) assetTotalEl.textContent = total;

  // Store total for nav arrows
  updateNavArrowsVisibility(total);

  // Load and display the first asset
  loadAsset(currentAssetIndex);
}

/**
 * Close the asset viewer
 */
export function closeAssetViewer() {
  isAnimating = false;
  stopAudio();

  // Dispatch event to unload current model BEFORE resetting state
  window.dispatchEvent(new CustomEvent('unloadCurrentModel'));

  resetAnimationState();

  assetViewerScreen.classList.remove('visible');

  if (canvasContainer) {
    canvasContainer.classList.add('hidden');
    canvasContainer.classList.remove('asset-mode');
  }

  hideArButton();

  // Hide all UI elements
  if (assetInfo) {
    assetInfo.classList.add('hidden');
    assetInfo.classList.remove('visible');
  }
  if (assetNameCapsule) {
    assetNameCapsule.classList.add('hidden');
    assetNameCapsule.classList.remove('visible');
  }
  if (assetNameHint) {
    assetNameHint.classList.add('hidden');
    assetNameHint.classList.remove('visible');
  }
  closeInfoPopup();
  closeAssetList();
  if (assetPrevBtn) assetPrevBtn.classList.add('hidden');
  if (assetNextBtn) assetNextBtn.classList.add('hidden');
  if (assetArContainer) assetArContainer.classList.add('hidden');

  currentBuildingId = null;
  currentAssetIndex = 0;
  currentAsset = null;

  window.dispatchEvent(new CustomEvent('assetViewerClosed'));
}

/**
 * Load and display an asset with the full animation sequence
 */
async function loadAsset(index) {
  if (isAnimating) return;
  isAnimating = true;

  const asset = getAssetByIndex(currentBuildingId, index);
  if (!asset) {
    console.error('[AssetViewer] Asset not found at index:', index);
    isAnimating = false;
    return;
  }

  currentAsset = asset;
  currentAssetIndex = index;

  // Prime audio immediately during user gesture context (before any awaits).
  // Mobile browsers require play() to be called within a user gesture;
  // the animation delays that follow would expire that gesture window.
  primeAudio(asset);

  updateAssetInfo(asset, index);
  resetAnimationState();

  await runAnimationSequence(asset);

  isAnimating = false;
}

/**
 * Run the full animation sequence for an asset
 * Flow: B&W image → color reveal (5s) → fade to black → 3D model → audio
 */
async function runAnimationSequence(asset) {
  console.log('[AssetViewer] Starting animation for:', localized(asset.name));

  // Step 1: Load and show B&W image
  await loadImages(asset);
  await delay(100);

  // Step 2: Start reveal animation
  startRevealAnimation();

  // Pre-load 3D model while animation plays
  dispatchLoadModel(asset);

  // Step 3: Wait for reveal to finish, then fade
  await delay(REVEAL_DURATION);

  // Start fade to black
  fadeOverlay.classList.add('active');
  await delay(FADE_DURATION);

  // Step 4: Show 3D model
  await delay(MODEL_SHOW_DELAY);
  show3DContainer();

  // Fade from black + start brightness fade-in together
  fadeOverlay.classList.remove('active');
  window.dispatchEvent(new CustomEvent('modelReveal'));
  await delay(FADE_DURATION);

  // Show info
  showAssetInfo();

  // Step 5: Play audio with delay
  if (localized(asset.audio)) {
    const audioDelay = asset.audioDelay || 500;
    await delay(audioDelay);
    playAudio(asset);
  }
}

/**
 * Load the B&W and masked images
 */
function loadImages(asset) {
  return new Promise((resolve) => {
    let loadedCount = 0;
    const totalToLoad = 3; // colored + masked + blur bg
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalToLoad) resolve();
    };

    // Blur background uses the colored image
    blurBgImage.onload = checkLoaded;
    blurBgImage.onerror = checkLoaded;
    blurBgImage.src = asset.coloredImage;

    coloredImage.onload = checkLoaded;
    coloredImage.onerror = checkLoaded;
    coloredImage.src = asset.coloredImage;

    maskedImage.onload = checkLoaded;
    maskedImage.onerror = checkLoaded;
    maskedImage.src = asset.maskedImage;

    imageRevealContainer.style.display = 'flex';
    if (canvasContainer) canvasContainer.classList.add('hidden');
  });
}

/**
 * Start the reveal animation (non-blocking)
 */
function startRevealAnimation() {
  maskedImage.style.setProperty('--reveal-duration', `${REVEAL_DURATION}ms`);
  maskedImage.classList.add('revealing');
}

/**
 * Show the 3D container (canvas container)
 */
function show3DContainer() {
  imageRevealContainer.style.display = 'none';

  if (canvasContainer) {
    canvasContainer.classList.remove('hidden');
    canvasContainer.classList.add('asset-mode');
  }

  if (asset3dContainer) {
    asset3dContainer.classList.add('visible');
  }

  showArButton();

  if (assetArContainer) assetArContainer.classList.remove('hidden');

  showNavArrows();

  console.log('[AssetViewer] Showing 3D container');
}

/**
 * Show appropriate AR button based on device
 * - iOS: Quick Look link (USDZ)
 * - Android with AR: WebXR AR button
 * - Desktop/no AR: same button, but opens QR popup (handled in main.js)
 */
function showArButton() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    if (assetIosArLink && currentAsset?.modelUsdz) {
      assetIosArLink.href = currentAsset.modelUsdz;
      assetIosArLink.classList.remove('hidden');
    }
    if (assetArButton) assetArButton.classList.add('hidden');
  } else {
    // Show AR button for both mobile (starts AR) and desktop (shows QR popup)
    if (assetArButton) assetArButton.classList.remove('hidden');
    if (assetIosArLink) assetIosArLink.classList.add('hidden');
  }
}

function hideArButton() {
  if (assetArButton) assetArButton.classList.add('hidden');
  if (assetIosArLink) assetIosArLink.classList.add('hidden');
  if (assetArContainer) assetArContainer.classList.add('hidden');
}

function showAssetInfo() {
  if (assetInfo) {
    assetInfo.classList.remove('hidden');
    assetInfo.classList.add('visible');
  }
  if (assetNameCapsule) {
    assetNameCapsule.classList.remove('hidden');
    assetNameCapsule.classList.add('visible');
  }
  // Show hint only if asset has info text
  if (assetNameHint && currentAsset && localized(currentAsset.infoText)) {
    assetNameHint.classList.remove('hidden');
    assetNameHint.classList.add('visible');
  }
}

function hideAssetInfo() {
  if (assetInfo) {
    assetInfo.classList.remove('visible');
    assetInfo.classList.add('hidden');
  }
  if (assetNameCapsule) {
    assetNameCapsule.classList.remove('visible');
    assetNameCapsule.classList.add('hidden');
  }
  if (assetNameHint) {
    assetNameHint.classList.remove('visible');
    assetNameHint.classList.add('hidden');
  }
  closeInfoPopup();
}

function openInfoPopup() {
  const infoText = localized(currentAsset?.infoText);
  if (!infoText) return;
  if (!assetInfoPopup) return;

  if (assetInfoPopupTitle) assetInfoPopupTitle.textContent = localized(currentAsset.name);
  if (assetInfoPopupText) {
    // Convert \n to <p> tags for paragraphs
    assetInfoPopupText.innerHTML = infoText
      .split('\n\n')
      .map(p => `<p style="margin:0 0 16px 0">${p}</p>`)
      .join('');
  }

  assetInfoPopup.classList.remove('hidden');
  // Force reflow before adding visible for transition
  assetInfoPopup.offsetHeight;
  assetInfoPopup.classList.add('visible');
}

function closeInfoPopup() {
  if (!assetInfoPopup) return;
  assetInfoPopup.classList.remove('visible');
  setTimeout(() => {
    assetInfoPopup.classList.add('hidden');
  }, 300);
}

function openAssetList() {
  if (!assetListPopup || !currentBuildingId) return;
  if (isAnimating) return;

  const building = getBuilding(currentBuildingId);
  if (!building || !building.assets.length) return;

  // Set title
  if (assetListPopupTitle) assetListPopupTitle.textContent = localized(building.name);

  // Populate items
  if (assetListPopupItems) {
    assetListPopupItems.innerHTML = '';
    building.assets.forEach((asset, index) => {
      const item = document.createElement('button');
      item.className = 'asset-list-item' + (index === currentAssetIndex ? ' active' : '');
      item.innerHTML = `<span class="asset-list-item-number">${index + 1}</span><span class="asset-list-item-name">${localized(asset.name)}</span>`;
      item.addEventListener('click', () => jumpToAsset(index));
      assetListPopupItems.appendChild(item);
    });
  }

  assetListPopup.classList.remove('hidden');
  assetListPopup.offsetHeight; // force reflow
  assetListPopup.classList.add('visible');
}

function closeAssetList() {
  if (!assetListPopup) return;
  assetListPopup.classList.remove('visible');
  setTimeout(() => {
    assetListPopup.classList.add('hidden');
  }, 300);
}

function jumpToAsset(index) {
  if (index === currentAssetIndex) {
    closeAssetList();
    return;
  }

  closeAssetList();

  // Same cleanup as navigateAsset
  stopAudio();
  hideAssetInfo();
  window.dispatchEvent(new CustomEvent('unloadCurrentModel'));
  loadAsset(index);
}

function updateAssetInfo(asset, index) {
  if (assetNameEl) assetNameEl.textContent = localized(asset.name);
  if (assetCurrentEl) assetCurrentEl.textContent = index + 1;
}

function updateNavArrowsVisibility(total) {
  window._assetTotal = total;
}

function showNavArrows() {
  const total = window._assetTotal || 0;
  if (total > 1) {
    if (assetPrevBtn) assetPrevBtn.classList.remove('hidden');
    if (assetNextBtn) assetNextBtn.classList.remove('hidden');
  }
}

function hideNavArrows() {
  if (assetPrevBtn) assetPrevBtn.classList.add('hidden');
  if (assetNextBtn) assetNextBtn.classList.add('hidden');
}

/**
 * Navigate to next/previous asset
 */
function navigateAsset(direction) {
  if (isAnimating) return;

  const total = getAssetCount(currentBuildingId);
  if (total <= 1) return;

  stopAudio();
  hideAssetInfo();

  let newIndex = currentAssetIndex + direction;
  if (newIndex < 0) newIndex = total - 1;
  if (newIndex >= total) newIndex = 0;

  window.dispatchEvent(new CustomEvent('unloadCurrentModel'));

  loadAsset(newIndex);
}

function goBackToBuildings() {
  closeAssetViewer();

  if (window.showBuildingsScreen) {
    const introScreen = document.getElementById('intro-screen');
    if (introScreen) introScreen.classList.add('hidden');

    const buildingsScreen = document.getElementById('buildings-screen');
    if (buildingsScreen) buildingsScreen.classList.add('visible');

    window.appState.currentScreen = 'buildings';
  }
}

/**
 * Reset animation state for new asset
 */
function resetAnimationState() {
  if (imageRevealWrapper) {
    imageRevealWrapper.style.transition = 'none';
    imageRevealWrapper.style.transform = 'scale(1)';
    imageRevealWrapper.style.transformOrigin = '50% 50%';
    imageRevealWrapper.offsetHeight;
  }

  if (maskedImage) {
    maskedImage.classList.remove('revealing');
    maskedImage.style.opacity = '0';
    maskedImage.style.clipPath = '';
  }

  if (fadeOverlay) {
    fadeOverlay.classList.remove('active');
  }

  if (asset3dContainer) {
    asset3dContainer.classList.remove('visible');
  }

  if (canvasContainer) {
    canvasContainer.classList.add('hidden');
    canvasContainer.classList.remove('asset-mode');
  }

  if (imageRevealContainer) {
    imageRevealContainer.style.display = 'flex';
  }

  hideAssetInfo();
  hideArButton();
  hideNavArrows();
}

function dispatchLoadModel(asset) {
  console.log('[AssetViewer] Dispatching loadAssetModel event for:', asset.model);
  window.dispatchEvent(new CustomEvent('loadAssetModel', {
    detail: {
      modelPath: asset.model,
      modelUsdz: asset.modelUsdz,
      hasAnimation: asset.hasAnimation,
      turntable: asset.turntable
    }
  }));
}

/**
 * Prime audio during user gesture so the browser "unlocks" it for later playback.
 * Called synchronously at the start of loadAsset, before any await delays.
 */
function primeAudio(asset) {
  const audioSrc = localized(asset.audio);
  if (!audioElement || !audioSrc) return;

  audioElement.src = audioSrc;
  audioElement.loop = asset.audioLoop || false;
  audioElement.currentTime = 0;

  // Mute and play to "unlock" the audio element within the user gesture.
  // Stays muted — only playAudio() unmutes at the right time.
  audioElement.muted = true;
  const p = audioElement.play();
  if (p) {
    p.then(() => {
      audioElement.pause();
      audioElement.currentTime = 0;
      console.log('[AssetViewer] Audio primed:', audioSrc);
    }).catch(() => {
      console.log('[AssetViewer] Audio prime failed (will retry on playAudio)');
    });
  }
}

function playAudio(asset) {
  const audioSrc = localized(asset.audio);
  if (!audioElement || !audioSrc) return;

  // Stale call from old animation sequence — ignore
  if (asset !== currentAsset) return;

  console.log('[AssetViewer] Playing audio:', audioSrc);

  // Source was already set and primed in loadAsset;
  // only re-set if somehow different (fallback)
  if (!audioElement.src.includes(audioSrc.replace(/^\//, ''))) {
    audioElement.src = audioSrc;
    audioElement.loop = asset.audioLoop || false;
  }

  audioElement.currentTime = 0;
  audioElement.volume = 1;
  audioElement.muted = false; // ensure unmuted (prime may not have resolved yet)

  // Reset mute state for new audio
  if (audioMuteBtn) {
    audioMuteBtn.classList.remove('muted');
    audioMuteBtn.classList.remove('hidden');
    audioMuteBtn.classList.add('visible');
  }

  audioElement.play().catch(err => {
    console.warn('[AssetViewer] Audio autoplay blocked:', err);
  });
}

function stopAudio() {
  if (audioElement) {
    audioElement.pause();
    audioElement.currentTime = 0;
    audioElement.muted = true;
  }
  // Hide and reset mute button
  if (audioMuteBtn) {
    audioMuteBtn.classList.remove('visible', 'muted');
    audioMuteBtn.classList.add('hidden');
  }
}

function toggleAudioMute() {
  if (!audioElement || !audioElement.src) return;

  if (audioElement.paused) {
    audioElement.play().catch(err => {
      console.warn('[AssetViewer] Audio resume blocked:', err);
    });
    if (audioMuteBtn) audioMuteBtn.classList.remove('muted');
  } else {
    audioElement.pause();
    if (audioMuteBtn) audioMuteBtn.classList.add('muted');
  }
}

/**
 * Get a localized value from an asset field.
 * Supports both old format (string) and new format ({ ro: '...', en: '...' }).
 */
function localized(value) {
  if (!value) return value;
  if (typeof value === 'string') return value;
  const lang = getCurrentLanguage();
  return value[lang] || value.ro || Object.values(value)[0];
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function getCurrentAsset() {
  return currentAsset;
}

export function getCurrentBuildingId() {
  return currentBuildingId;
}
